import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, XCircle, ArrowLeft, MinusCircle } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import LaTeXRenderer from '../../components/LaTeXRenderer';

function TestResults() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, [attemptId]);

  const fetchResults = async () => {
    try {
      const attemptRes = await api.get(`/attempts/${attemptId}`);
      const attemptData = attemptRes.data;
      setAttempt(attemptData);

      const testRes = await api.get(`/tests/${attemptData.test_id}`);
      setTest(testRes.data);

      const questionsRes = await api.get(`/tests/${attemptData.test_id}/questions`);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const getAnswerStatus = (answer) => {
    // Check if answer is empty/null/undefined
    const userAnswer = answer.answer;
    const isEmpty = userAnswer === '' || userAnswer === null || userAnswer === undefined;
    
    if (isEmpty) {
      return 'unanswered';
    }
    return answer.is_correct ? 'correct' : 'incorrect';
  };

  const getStatusBadge = (status) => {
    if (status === 'correct') {
      return <Badge className='bg-green-100 text-green-800'>Correct</Badge>;
    } else if (status === 'unanswered') {
      return <Badge className='bg-gray-100 text-gray-800'>Unanswered</Badge>;
    } else {
      return <Badge className='bg-red-100 text-red-800'>Incorrect</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'correct') {
      return <CheckCircle className='w-6 h-6 text-green-600' />;
    } else if (status === 'unanswered') {
      return <MinusCircle className='w-6 h-6 text-gray-400' />;
    } else {
      return <XCircle className='w-6 h-6 text-red-600' />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-96'>
          <div className='spinner'></div>
        </div>
      </Layout>
    );
  }

  if (!attempt || !test) {
    return (
      <Layout>
        <div className='text-center py-12'>
          <p className='text-gray-500'>Results not found</p>
        </div>
      </Layout>
    );
  }

  // Check if results should be shown
  if (!test.config?.show_results_immediately) {
    return (
      <Layout>
        <div className='max-w-2xl mx-auto py-12'>
          <Card className='p-8 text-center'>
            <div className='mb-6'>
              <div className='w-20 h-20 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center'>
                <CheckCircle className='w-10 h-10 text-blue-600' />
              </div>
              <h2 className='text-2xl font-bold text-gray-900 mb-2'>Test Submitted Successfully</h2>
              <p className='text-gray-600'>
                Your test has been submitted and is being evaluated by the admin.
              </p>
            </div>
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
              <p className='text-sm text-blue-800'>
                <strong>Note:</strong> Results for this test will be shared by the administrator.
                You will be notified once your results are available.
              </p>
            </div>
            <Button onClick={() => navigate('/dashboard')}>
              <ArrowLeft className='w-4 h-4 mr-2' />
              Back to Dashboard
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  const correctAnswers = attempt.answers?.filter(a => a.is_correct).length || 0;
  const incorrectAnswers = attempt.answers?.filter(a => {
    const userAnswer = a.answer;
    const isEmpty = userAnswer === '' || userAnswer === null || userAnswer === undefined;
    return !a.is_correct && !isEmpty;
  }).length || 0;
  const unansweredCount = attempt.answers?.filter(a => {
    const userAnswer = a.answer;
    return userAnswer === '' || userAnswer === null || userAnswer === undefined;
  }).length || 0;
  const totalQuestions = test.question_ids?.length || 0;

  return (
    <Layout>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <h1 className='text-3xl font-bold text-gray-900'>Test Results</h1>
          <Button
            variant='outline'
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className='w-4 h-4 mr-2' />
            Back to Dashboard
          </Button>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Card className='p-6'>
            <h2 className='text-xl font-semibold mb-4'>{test.title}</h2>
            <div className='space-y-3'>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Status:</span>
                <Badge variant={attempt.status === 'completed' ? 'success' : 'warning'}>
                  {attempt.status === 'completed' ? 'Completed' : attempt.status === 'terminated' ? 'Terminated' : 'In Progress'}
                </Badge>
              </div>
              {attempt.status === 'terminated' && attempt.termination_reason && (
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Reason:</span>
                  <span className='text-sm text-red-600'>{attempt.termination_reason}</span>
                </div>
              )}
              <div className='flex justify-between'>
                <span className='text-gray-600'>Score:</span>
                <span className='font-semibold'>{attempt.score} / {test.total_points || totalQuestions}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Percentage:</span>
                <span className='font-semibold'>{attempt.percentage?.toFixed(2)}%</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Pass Percentage:</span>
                <span>{test.config?.pass_percentage || 40}%</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Result:</span>
                <Badge variant={attempt.percentage >= (test.config?.pass_percentage || 40) ? 'success' : 'destructive'}>
                  {attempt.percentage >= (test.config?.pass_percentage || 40) ? 'PASS' : 'FAIL'}
                </Badge>
              </div>
            </div>
          </Card>

          <Card className='p-6'>
            <h2 className='text-xl font-semibold mb-4'>Answer Summary</h2>
            <div className='space-y-4'>
              <div className='flex items-center justify-between p-3 bg-green-50 rounded-lg'>
                <div className='flex items-center'>
                  <CheckCircle className='w-5 h-5 text-green-600 mr-2' />
                  <span className='text-green-800 font-medium'>Correct</span>
                </div>
                <span className='text-green-900 font-bold'>{correctAnswers}</span>
              </div>
              <div className='flex items-center justify-between p-3 bg-red-50 rounded-lg'>
                <div className='flex items-center'>
                  <XCircle className='w-5 h-5 text-red-600 mr-2' />
                  <span className='text-red-800 font-medium'>Incorrect</span>
                </div>
                <span className='text-red-900 font-bold'>{incorrectAnswers}</span>
              </div>
              <div className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                <div className='flex items-center'>
                  <MinusCircle className='w-5 h-5 text-gray-500 mr-2' />
                  <span className='text-gray-700 font-medium'>Unanswered</span>
                </div>
                <span className='text-gray-900 font-bold'>{unansweredCount}</span>
              </div>
              <div className='flex items-center justify-between p-3 bg-blue-50 rounded-lg border-2 border-blue-300'>
                <span className='text-blue-800 font-medium'>Total Questions</span>
                <span className='text-blue-900 font-bold'>{totalQuestions}</span>
              </div>
            </div>
          </Card>
        </div>

        <Card className='p-6'>
          <h2 className='text-xl font-semibold mb-4'>Detailed Answers</h2>
          <div className='space-y-4'>
            {attempt.answers?.map((answer, index) => {
              const question = questions.find(q => q.id === answer.question_id);
              if (!question) return null;

              const status = getAnswerStatus(answer);
              const userAnswerIndex = answer.answer;
              const userAnswerText = (typeof userAnswerIndex === 'number' && question.choices) 
                ? question.choices[userAnswerIndex] 
                : (userAnswerIndex || 'Not answered');

              return (
                <div key={answer.question_id} className='border rounded-lg p-4 hover:bg-gray-50'>
                  <div className='flex items-start justify-between mb-3'>
                    <div className='flex items-start flex-1'>
                      <div className='mr-3 mt-1'>
                        {getStatusIcon(status)}
                      </div>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2 mb-2'>
                          <span className='font-semibold text-gray-700'>Q{index + 1}.</span>
                          {getStatusBadge(status)}
                        </div>
                        <div className='text-gray-900 mb-3'>
                          <LaTeXRenderer text={question.question_text} />
                        </div>
                      </div>
                    </div>
                    <div className='text-right ml-4'>
                      <span className={status === 'correct' ? 'text-green-600' : status === 'unanswered' ? 'text-gray-600' : 'text-red-600'}>
                        {status === 'unanswered' ? '0' : (answer.is_correct ? '+' : '')}{answer.points_earned || 0}/{question.points || 1}
                      </span>
                    </div>
                  </div>

                  {question.choices && question.choices.length > 0 && (
                    <div className='space-y-2 ml-9'>
                      {question.choices.map((choice, idx) => {
                        const isUserAnswer = userAnswerIndex === idx || userAnswerText === choice;
                        const isCorrectAnswer = question.correct_answer === choice;

                        return (
                          <div
                            key={idx}
                            className={`p-2 rounded flex items-center ${
                              isCorrectAnswer
                                ? 'bg-green-50 border border-green-200'
                                : isUserAnswer && !isCorrectAnswer
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <span className='w-6 text-center font-medium mr-2'>{String.fromCharCode(65 + idx)}.</span>
                            <div className={`flex-1 ${isCorrectAnswer ? 'text-green-800' : isUserAnswer ? 'text-red-800' : 'text-gray-700'}`}>
                              <LaTeXRenderer text={choice} />
                            </div>
                            {isUserAnswer && (
                              <span className='ml-auto text-sm font-medium text-gray-600'>
                                Your answer
                              </span>
                            )}
                            {isCorrectAnswer && (
                              <span className='ml-auto text-sm font-medium text-green-600'>
                                Correct answer
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {question.explanation && (
                    <div className='mt-3 ml-9 p-3 bg-blue-50 rounded border border-blue-200'>
                      <p className='text-sm text-blue-900'>
                        <strong>Explanation:</strong> {question.explanation}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default TestResults;